/**
 * Epic 1 — manifest and carrier invoice upload UI.
 *
 * PRS Epic 1 AC #8:  two drop zones (manifest CSV + carrier invoice CSV/XML).
 * PRS Epic 1 AC #9:  carrier invoice zone disabled until carrier is selected.
 * PRS Epic 1 AC #10: processing state polled via TanStack Query.
 * PRS Epic 1 AC #11: ingestion report shown on completion.
 */

import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { IconDownload } from "@tabler/icons-react";

import { type FileStatusResponse, useApiClient } from "@/lib/api-client";

// ── Constants ─────────────────────────────────────────────────────────────────

const CARRIERS = [
  { value: "fedex", label: "FedEx" },
  { value: "ups", label: "UPS" },
  { value: "dhl", label: "DHL" },
] as const;

type CarrierValue = (typeof CARRIERS)[number]["value"];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

const POLL_INTERVAL_MS = 2_000;

// ── Helpers ───────────────────────────────────────────────────────────────────

function validateFile(file: File, allowedExts: readonly string[]): string | null {
  const ext = ("." + (file.name.split(".").pop() ?? "")).toLowerCase();
  if (!allowedExts.includes(ext)) {
    return `Unsupported file type "${ext}". Accepted: ${allowedExts.join(", ")}.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File exceeds 25 MB limit (${(file.size / 1_048_576).toFixed(1)} MB).`;
  }
  return null;
}

function isActiveStatus(s: string | undefined): boolean {
  return s === "QUEUED" || s === "PROCESSING";
}

// ── DropZone ──────────────────────────────────────────────────────────────────

interface DropZoneProps {
  /** Allowed file extensions, e.g. [".csv"] or [".csv", ".xml"]. */
  accept: readonly string[];
  /** When true the zone ignores interaction (carrier not yet selected). */
  disabled?: boolean;
  file: File | null;
  validationError: string | null;
  onFile: (file: File | null, error: string | null) => void;
  /** Accessible label for the interactive region. */
  dropLabel: string;
}

function DropZone({
  accept,
  disabled = false,
  file,
  validationError,
  onFile,
  dropLabel,
}: DropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function pick(f: File) {
    const err = validateFile(f, accept);
    onFile(err ? null : f, err);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const f = e.dataTransfer.files[0];
    if (f) pick(f);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) pick(f);
    e.target.value = ""; // allow re-selecting the same file
  }

  const baseClass =
    "relative flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors";
  const stateClass = disabled
    ? "cursor-not-allowed border-border opacity-40"
    : dragging
      ? "cursor-copy border-accent bg-bg-3"
      : file
        ? "cursor-pointer border-status-match bg-bg-2"
        : validationError
          ? "cursor-pointer border-status-ghost"
          : "cursor-pointer border-border hover:border-border-2";

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={dropLabel}
        aria-disabled={disabled}
        className={`${baseClass} ${stateClass}`}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept.join(",")}
          className="sr-only"
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
          onChange={handleChange}
        />

        {/* File icon — inline SVG, no icon-library dependency */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
          className={file ? "text-status-match" : "text-text-3"}
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>

        {file ? (
          <div className="flex flex-col items-center gap-1">
            <p className="max-w-[200px] truncate font-mono text-sm text-text">{file.name}</p>
            <p className="text-xs text-text-2">{(file.size / 1_024).toFixed(1)} KB</p>
            <button
              type="button"
              className="text-xs text-text-2 underline underline-offset-2 hover:text-text"
              onClick={(e) => {
                e.stopPropagation();
                onFile(null, null);
              }}
              aria-label={`Remove ${file.name}`}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-sm text-text-2">
              Drop file or{" "}
              <span className="text-text underline underline-offset-2">browse</span>
            </p>
            <p className="mt-1 font-mono text-[11px] text-text-3">
              {accept.join(" · ")} · max 25 MB
            </p>
          </div>
        )}
      </div>

      {validationError && (
        <p role="alert" className="mt-1.5 text-xs text-status-ghost">
          {validationError}
        </p>
      )}
    </div>
  );
}

// ── JobStatusCard ─────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  QUEUED: "badge-pending",
  PROCESSING: "badge-pending",
  COMPLETE: "badge-match",
  FAILED: "badge-ghost",
};

function JobStatusCard({ data }: { data: FileStatusResponse }) {
  const badgeClass = STATUS_BADGE[data.status] ?? "badge";
  const statusLabel =
    data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase();

  return (
    <div
      className="mt-2 rounded-lg border border-border bg-bg-2 p-4"
      aria-live="polite"
      aria-label={`Processing status: ${statusLabel}`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="max-w-[180px] truncate font-mono text-xs text-text-2">
          {data.filename}
        </span>
        <span className={badgeClass}>{statusLabel}</span>
      </div>

      {/* Animated progress bar while the task is running */}
      {isActiveStatus(data.status) && (
        <div
          role="progressbar"
          aria-label="Processing"
          className="h-1 w-full overflow-hidden rounded-full bg-bg-3"
        >
          <div className="h-full w-1/3 animate-pulse rounded-full bg-status-pending" />
        </div>
      )}

      {/* Ingestion report — Epic 1 AC #11 */}
      {data.status === "COMPLETE" && (
        <dl className="grid grid-cols-3 gap-2">
          {(
            [
              ["Received", data.row_count],
              ["Written", data.rows_written],
              ["Skipped", data.rows_skipped],
            ] as [string, number | null][]
          ).map(([label, value]) => (
            <div key={label} className="rounded bg-bg-3 p-2">
              <dt className="text-[10px] uppercase tracking-wide text-text-3">{label}</dt>
              <dd className="mt-0.5 font-mono text-sm font-semibold text-text">
                {value ?? "—"}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {data.status === "FAILED" && data.error_detail && (
        <p
          className="mt-1 font-mono text-xs text-status-ghost"
          title={data.error_detail}
        >
          {data.error_detail.length > 120
            ? data.error_detail.slice(0, 120) + "…"
            : data.error_detail}
        </p>
      )}
    </div>
  );
}

// ── UploadPage ────────────────────────────────────────────────────────────────

export function UploadPage() {
  const { uploadManifest, uploadInvoice, getFileStatus } = useApiClient();

  // Manifest zone
  const [manifestFile, setManifestFile] = useState<File | null>(null);
  const [manifestFileError, setManifestFileError] = useState<string | null>(null);
  const [manifestJobId, setManifestJobId] = useState<string | null>(null);

  // Carrier invoice zone
  const [carrier, setCarrier] = useState<CarrierValue | "">("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceFileError, setInvoiceFileError] = useState<string | null>(null);
  const [invoiceJobId, setInvoiceJobId] = useState<string | null>(null);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const manifestMutation = useMutation({
    mutationFn: (file: File) => uploadManifest(file),
    onSuccess(data) {
      setManifestJobId(data.file_id);
      setManifestFile(null);
    },
  });

  const invoiceMutation = useMutation({
    mutationFn: ({ file, carrierId }: { file: File; carrierId: string }) =>
      uploadInvoice(file, carrierId),
    onSuccess(data) {
      setInvoiceJobId(data.file_id);
      setInvoiceFile(null);
    },
  });

  // ── Status polling — Epic 1 AC #10 ────────────────────────────────────────

  const manifestStatus = useQuery({
    queryKey: ["file-status", manifestJobId],
    queryFn: () => getFileStatus(manifestJobId!),
    enabled: Boolean(manifestJobId),
    refetchInterval: (query) =>
      isActiveStatus(query.state.data?.status) ? POLL_INTERVAL_MS : false,
  });

  const invoiceStatus = useQuery({
    queryKey: ["file-status", invoiceJobId],
    queryFn: () => getFileStatus(invoiceJobId!),
    enabled: Boolean(invoiceJobId),
    refetchInterval: (query) =>
      isActiveStatus(query.state.data?.status) ? POLL_INTERVAL_MS : false,
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  const carrierLabel = CARRIERS.find((c) => c.value === carrier)?.label ?? carrier;

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h1 className="text-base font-semibold text-text">Upload files</h1>
        <p className="mt-1 text-sm text-text-2">
          Upload your manifest and carrier invoice to start reconciliation.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* ── Manifest zone ──────────────────────────────────────────────── */}
        <section aria-labelledby="manifest-heading">
          <div className="mb-3">
            <h2 id="manifest-heading" className="text-sm font-medium text-text">
              Manifest
            </h2>
            <p className="mt-0.5 text-xs text-text-2">Internal shipment data — CSV</p>
          </div>

          <DropZone
            accept={[".csv"]}
            file={manifestFile}
            validationError={manifestFileError}
            onFile={(f, err) => {
              setManifestFile(f);
              setManifestFileError(err);
            }}
            dropLabel="Drop manifest CSV or click to browse"
          />

          {/* Sample manifest — demonstrates multi-parcel shipments (one row per parcel,
              shipment-level fields on the group's first row). */}
          <a
            href="/samples/manifest_multiparcel.csv"
            download
            aria-label="Download sample manifest CSV (multi-parcel example)"
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-text-2 underline underline-offset-2 hover:text-text transition-colors"
          >
            <IconDownload size={13} aria-hidden />
            Download sample manifest
          </a>

          {manifestFile && !manifestFileError && (
            <button
              type="button"
              disabled={manifestMutation.isPending}
              className="mt-3 w-full rounded bg-accent px-4 py-2 text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => manifestMutation.mutate(manifestFile)}
            >
              {manifestMutation.isPending ? "Uploading…" : "Upload manifest"}
            </button>
          )}

          {manifestMutation.isError && (
            <p role="alert" className="mt-1.5 text-xs text-status-ghost">
              {manifestMutation.error.message}
            </p>
          )}

          {manifestJobId && manifestStatus.data && (
            <JobStatusCard data={manifestStatus.data} />
          )}
        </section>

        {/* ── Carrier invoice zone ────────────────────────────────────────── */}
        <section aria-labelledby="invoice-heading">
          <div className="mb-3">
            <h2 id="invoice-heading" className="text-sm font-medium text-text">
              Carrier invoice
            </h2>
            <p className="mt-0.5 text-xs text-text-2">FedEx, UPS, or DHL — CSV or XML</p>
          </div>

          {/* Carrier selector — Epic 1 AC #9 */}
          <div className="mb-3">
            <label htmlFor="carrier-select" className="sr-only">
              Carrier
            </label>
            <select
              id="carrier-select"
              value={carrier}
              className="w-full rounded border border-border bg-bg-3 px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
              onChange={(e) => {
                setCarrier(e.target.value as CarrierValue | "");
                // Reset invoice selection when carrier changes
                setInvoiceFile(null);
                setInvoiceFileError(null);
              }}
            >
              <option value="" disabled>
                Select carrier…
              </option>
              {CARRIERS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <DropZone
            accept={[".csv", ".xml"]}
            disabled={!carrier}
            file={invoiceFile}
            validationError={invoiceFileError}
            onFile={(f, err) => {
              setInvoiceFile(f);
              setInvoiceFileError(err);
            }}
            dropLabel="Drop carrier invoice CSV or XML or click to browse"
          />

          {invoiceFile && !invoiceFileError && carrier && (
            <button
              type="button"
              disabled={invoiceMutation.isPending}
              className="mt-3 w-full rounded bg-accent px-4 py-2 text-sm font-medium text-bg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() =>
                invoiceMutation.mutate({ file: invoiceFile, carrierId: carrier })
              }
            >
              {invoiceMutation.isPending
                ? "Uploading…"
                : `Upload ${carrierLabel} invoice`}
            </button>
          )}

          {invoiceMutation.isError && (
            <p role="alert" className="mt-1.5 text-xs text-status-ghost">
              {invoiceMutation.error.message}
            </p>
          )}

          {invoiceJobId && invoiceStatus.data && (
            <JobStatusCard data={invoiceStatus.data} />
          )}
        </section>
      </div>
    </div>
  );
}
